/**
 * BhiPlateBridgeCard.jsx — iter739 (Feb 2026)
 *
 * BHI → Plate Simulator bridge widget.
 *
 * Reads /api/breast-intelligence/plate-suggestions and renders 4-6 curated
 * food cards, each with:
 *   - Food name (BG)
 *   - Which burden axis it addresses + current burden value
 *   - "Добави в чинията" button that deep-links to /dashboard/diary?focus=
 *     plate-simulator&prefill=<alias> — the MealLog food search auto-opens
 *     with the alias pre-filled via the existing `meal-log:focus-search`
 *     event. One click to add.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Loader2, Utensils, ArrowRight, Info, PlusCircle, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/App";

const SEVERITY_COLORS = {
  red:               { bg: "bg-red-500/10",   text: "text-red-600 dark:text-red-300",   border: "border-red-500/30",   dot: "bg-red-500" },
  yellow:            { bg: "bg-amber-500/10", text: "text-amber-700 dark:text-amber-300", border: "border-amber-500/30", dot: "bg-amber-500" },
  green:             { bg: "bg-emerald-500/10", text: "text-emerald-700 dark:text-emerald-300", border: "border-emerald-500/30", dot: "bg-emerald-500" },
  insufficient_data: { bg: "bg-slate-500/10",  text: "text-slate-600 dark:text-slate-300", border: "border-slate-500/30", dot: "bg-slate-400" },
};

export default function BhiPlateBridgeCard({ isDark = false }) {
  const { API, user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  // iter740 — weekly plan state
  const [weekly, setWeekly] = useState(null);
  const [weeklyLoading, setWeeklyLoading] = useState(false);

  useEffect(() => {
    if (!user) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/breast-intelligence/plate-suggestions`);
        if (alive) setData(data);
      } catch (e) {
        if (alive) setErr(e?.response?.data?.detail || e.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [API, user]);

  const openInPlate = (alias) => {
    const url = `/dashboard/diary?focus=plate-simulator&prefill=${encodeURIComponent(alias)}`;
    navigate(url);
  };

  // iter740 — batch-add: store the list in sessionStorage; DiaryPage
  // reads it and dispatches meal-log:focus-search per item so the user
  // adds them one after another without navigating between pages.
  const batchAddAll = (aliases, contextLabel) => {
    if (!aliases || !aliases.length) return;
    try {
      sessionStorage.setItem("bhi_batch_add", JSON.stringify({
        aliases,
        context: contextLabel || "BHI препоръки",
        ts: Date.now(),
      }));
    } catch { /* sessionStorage might be blocked */ }
    toast.success(`Подготвени ${aliases.length} храни — отваряме плочата...`);
    navigate("/dashboard/diary?focus=plate-simulator&batch=bhi");
  };

  // iter740 — fetch weekly plan on demand
  const loadWeekly = async () => {
    if (weeklyLoading) return;
    setWeeklyLoading(true);
    try {
      const { data } = await axios.get(`${API}/breast-intelligence/weekly-plan`);
      setWeekly(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Не успяхме да заредим седмичния план");
    } finally {
      setWeeklyLoading(false);
    }
  };

  if (!user) return null;

  if (loading) {
    return (
      <div className={`rounded-2xl border p-4 ${isDark ? "border-white/10 bg-white/5" : "border-slate-200 bg-white"}`}
        data-testid="bhi-plate-bridge-loading">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Зареждане на препоръчано меню...
        </div>
      </div>
    );
  }

  if (err || !data) {
    return null;  // silent — this is an enhancement, not a critical block
  }

  if (data.state === "insufficient_data") {
    return (
      <div
        className={`rounded-2xl border p-5 ${isDark ? "border-white/10 bg-white/5" : "border-slate-200 bg-white"}`}
        data-testid="bhi-plate-bridge-empty"
      >
        <div className="flex items-start gap-3">
          <Utensils className="h-5 w-5 shrink-0 text-pink-500" />
          <div>
            <h3 className={`font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
              🥗 Ястия за подкрепа на гърдата
            </h3>
            <p className={`mt-2 text-sm ${isDark ? "text-slate-300" : "text-slate-600"}`}>
              {data.message_bg}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const isBaseline = data.state === "baseline";

  const suggestions = data.suggestions || [];
  const topAxes = data.top_axes || [];

  return (
    <section
      className={`rounded-2xl border p-5 ${isDark ? "border-white/10 bg-white/5" : "border-slate-200 bg-white"}`}
      data-testid="bhi-plate-bridge"
    >
      <header className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h3 className={`text-lg font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
            🥗 Ястия за подкрепа на гърдата
          </h3>
          <p className={`mt-1 text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
            {isBaseline
              ? "Базови препоръки за Ден 0 — безопасни универсални храни. Ще се персонализират след лаборатории и 14-дневен дневник."
              : "Препоръчано на база твоята текуща BHI екосистема — не общи съвети."}
          </p>
        </div>
        {/* iter740 — batch + weekly-plan action row */}
        <div className="flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => batchAddAll(suggestions.map((s) => s.alias), "BHI top препоръки")}
            data-testid="bhi-plate-batch-add"
            disabled={!suggestions.length}
            className="inline-flex items-center gap-1.5 rounded-lg border border-pink-500/40 bg-pink-500/10 px-3 py-1.5 text-xs font-semibold text-pink-600 transition hover:bg-pink-500/20 disabled:opacity-50 dark:text-pink-300"
          >
            <PlusCircle className="h-3.5 w-3.5" /> Добави всички
          </button>
          <button
            type="button"
            onClick={loadWeekly}
            data-testid="bhi-plate-weekly-plan"
            disabled={weeklyLoading}
            className="inline-flex items-center gap-1.5 rounded-lg border border-purple-500/40 bg-purple-500/10 px-3 py-1.5 text-xs font-semibold text-purple-600 transition hover:bg-purple-500/20 disabled:opacity-60 dark:text-purple-300"
          >
            {weeklyLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CalendarDays className="h-3.5 w-3.5" />}
            {weekly ? "Опресни план" : "Направи седмичен план"}
          </button>
        </div>
      </header>

      {/* Top burden axes summary */}
      {topAxes.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2" data-testid="bhi-plate-bridge-axes">
          {topAxes.map((a) => {
            const c = SEVERITY_COLORS[a.severity] || SEVERITY_COLORS.insufficient_data;
            return (
              <div
                key={a.axis}
                className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium ${c.bg} ${c.text} border ${c.border}`}
              >
                <span className={`inline-block h-2 w-2 rounded-full ${c.dot}`} />
                <span>{a.label_bg}</span>
                <span className="opacity-70">·</span>
                <span>тежест {a.burden}/100</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Food suggestion grid */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {suggestions.map((s, i) => {
          const c = SEVERITY_COLORS[s.severity] || SEVERITY_COLORS.insufficient_data;
          return (
            <div
              key={`${s.name}-${i}`}
              data-testid={`bhi-plate-food-${i}`}
              className={`rounded-xl border p-3 ${isDark ? "border-white/10 bg-slate-900/40" : "border-slate-200 bg-slate-50"}`}
            >
              <div className="mb-2 flex items-center justify-between gap-2">
                <div className={`text-sm font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
                  {s.name}
                </div>
                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${c.bg} ${c.text}`}>
                  {s.axis_label}
                </span>
              </div>
              <p className={`text-xs leading-relaxed ${isDark ? "text-slate-300" : "text-slate-600"}`}>
                {s.why_bg}
              </p>
              {/* iter747 — NIS/DIL badges so patients see the actual metabolic cost of the food */}
              {(s.nis != null || s.dil != null) && (
                <div className="mt-1.5 flex flex-wrap gap-1 text-[10px]">
                  {s.nis != null && (
                    <span className={`inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 ${
                      s.nis <= 10 ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300"
                      : s.nis <= 20 ? "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300"
                      : "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300"
                    }`} title="Нутрамило Инсулинов Скор (NIS)">
                      NIS {s.nis}
                    </span>
                  )}
                  {s.dil != null && (
                    <span className={`inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 ${
                      s.dil <= 10 ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300"
                      : s.dil <= 20 ? "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300"
                      : "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300"
                    }`} title="Диетичен инсулинов товар (DIL)">
                      DIL {s.dil}
                    </span>
                  )}
                </div>
              )}
              <div className={`mt-2 flex items-center justify-between text-[10px] ${isDark ? "text-slate-500" : "text-slate-400"}`}>
                <span>Източник: {s.twin_source}</span>
              </div>
              <button
                type="button"
                onClick={() => openInPlate(s.alias)}
                data-testid={`bhi-plate-food-add-${i}`}
                className="mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-gradient-to-r from-pink-500 to-purple-500 px-3 py-2 text-xs font-semibold text-white shadow-sm transition hover:from-pink-600 hover:to-purple-600"
              >
                Добави в чинията <ArrowRight className="h-3.5 w-3.5" />
              </button>
            </div>
          );
        })}
      </div>

      {data.note_bg ? (
        <div className={`mt-4 flex items-start gap-2 rounded-lg p-2 text-[11px] ${isDark ? "bg-white/5 text-slate-400" : "bg-slate-50 text-slate-500"}`}>
          <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          <span>{data.note_bg}</span>
        </div>
      ) : null}

      {/* iter740 — Weekly plan panel */}
      {weekly && weekly.state === "ok" && (
        <div
          className={`mt-5 rounded-xl border p-4 ${isDark ? "border-purple-500/30 bg-purple-500/5" : "border-purple-200 bg-purple-50/40"}`}
          data-testid="bhi-weekly-plan"
        >
          <div className="mb-3 flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-purple-500" />
            <h4 className={`text-sm font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
              7-дневен ротационен план
            </h4>
          </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {(weekly.days || []).map((d) => (
              <div
                key={d.day_index}
                className={`rounded-lg border p-3 ${isDark ? "border-white/10 bg-slate-900/40" : "border-slate-200 bg-white"}`}
                data-testid={`bhi-weekly-day-${d.day_index}`}
              >
                <div className={`text-xs font-bold ${isDark ? "text-white" : "text-slate-900"}`}>{d.weekday}</div>
                <div className={`mb-1 text-[10px] uppercase tracking-wider ${isDark ? "text-purple-300" : "text-purple-600"}`}>
                  {d.focus_bg}
                </div>
                <ul className={`space-y-0.5 text-[11px] ${isDark ? "text-slate-300" : "text-slate-600"}`}>
                  {(d.foods || []).map((f, i) => (
                    <li key={`${d.day_index}-${i}`}>• {f.name}</li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={() => batchAddAll((d.foods || []).map((f) => f.alias), `План · ${d.weekday}`)}
                  className="mt-2 inline-flex w-full items-center justify-center gap-1 rounded-md bg-purple-500/10 px-2 py-1 text-[10px] font-semibold text-purple-600 transition hover:bg-purple-500/20 dark:text-purple-300"
                  data-testid={`bhi-weekly-day-add-${d.day_index}`}
                >
                  <PlusCircle className="h-3 w-3" /> Добави дневните
                </button>
              </div>
            ))}
          </div>
          {weekly.note_bg && (
            <p className={`mt-3 text-[10px] italic ${isDark ? "text-slate-400" : "text-slate-500"}`}>{weekly.note_bg}</p>
          )}
        </div>
      )}
      {weekly && weekly.state === "insufficient_data" && (
        <div className="mt-4 rounded-lg border border-dashed border-slate-300 bg-slate-50 p-3 text-xs text-slate-600 dark:bg-slate-900/40">
          {weekly.message_bg}
        </div>
      )}
    </section>
  );
}
